import { makeAutoObservable, observable, toJS } from 'mobx';
import React from 'react';

import {
	AlgorithmTypeOption,
	AlgorithmValue,
	Configuration,
	defaultAlgorithmValue,
	Form,
	FormBuilderValue,
	FormBuilderValueType,
	isActionMode,
	Property,
	PropertyType,
	SESSION_PERSISTED_PROPERTY_IDS,
} from './models';
import { MobxManager } from 'framework/mobx-integration';
import { Section } from '../../layout/section';
import { FormLayoutType } from '../../layout/formLayout';
import { flatTree, getItemByKey } from 'tools/tree';
import { renderProperty } from 'controls/react/form/formBuilderNew/components';
import { apiFetch, ApiRequest } from 'framework/api';
import { IconType, openConfirmMessage } from 'controls/react/ant/antConfirmModal';
import { ModalPosition } from 'controls/react/ant/antModal';
import { ApplicationState } from 'framework/applicationState';
import { translator } from 'core/localization/localization';
import { get } from 'lodash';
import { AnnotationValidationMethod, AnnotationValidator, TestResult } from './annotationValidator';

const DEBUG_MODE = false;

// Shared by whoever creates several sibling FormBuilderStore instances in one session (e.g. the SQ wizard's
// "create multiple qualifiers" flow) and wants SESSION_PERSISTED_PROPERTY_IDS to carry over between them — pass the
// same instance via `sessionPersistedValues` in each store's options. Ported from the old formBuilder's
// SESSION_PERSISTED constant + qualifierWizard's reduxReducer `persisted` state; scoped explicitly here (instead of
// a module-level singleton) so unrelated forms elsewhere in the app never leak values into each other.
export class SessionPersistedValues {
	values: Partial<Record<string, FormBuilderValueType>> = {};

	constructor() {
		makeAutoObservable(this);
	}
}

const log = (...args: unknown[]) => {
	if (DEBUG_MODE) {
		console.log(...args);
	}
};

const propertiesWithDataSource: Partial<Record<PropertyType, boolean | ((property: Property) => boolean)>> = {
	CESelection: true,
	CESelectionMulti: true,
	CEAuthentication: true,
	CETextInput: true,
	CESchedule: true,
	CEAlgorithm: true,
	CECollection: (property) => property.immutable,
};

const hasDataSource = (property: Property) => {
	const rule = propertiesWithDataSource[property.type];
	return typeof rule === 'function' ? rule(property) : !!rule;
};

const propertiesOnBlur = ['CETextInput'];
const propertiesClearNeededConfirmation = ['CECollection'];

const i18n = translator({});

interface FormBuilderOptions {
	configuration: Configuration;
	mode: 'update' | 'create';
	columnsCount?: number;
	columnsGroups?: { [index: number]: string[] };
	getPropertyData?: (
		propertyPath: string,
		value: FormBuilderValue,
	) => ApiRequest<string | { text: string; value: string }[]>;
	path?: string;
	// we need this only on old pages to notify parent
	onValidChange?: (x: boolean) => void;
	inline?: boolean;
	rootFormStore?: FormBuilderStore;
	timezone?: string;
	userSettingsCategory?: string;
	splitter?: boolean;
	splitByForms?: boolean;
	validateMethods?: { [key in AnnotationValidationMethod]: (value: FormBuilderValue) => ApiRequest<TestResult> };
	// slot for an externally-rendered element (e.g. SQ wizard's "measured target" picker); ported from the old
	// formBuilder's `multipleLink` prop, which the qualifier wizard group `grpMeasuredTarget` renders inline.
	measuredTargetSlot?: React.ReactNode;
	sessionPersistedValues?: SessionPersistedValues;
	// Intended as the asset type restriction for the inline "create new asset" window opened from the assetId
	// CESelection field — ported from the old formBuilder's connectDependencies.js reading `original.systemType`.
	// Currently unused: that window now reuses the shared AddAsset component (controls/react/windows/addAsset),
	// which has no systemType prop, and the underlying AssetForm/AddAssetForm React form never restricted the
	// systemType dropdown from a create-time option either — so this was already a no-op before that change.
	// Left in place since qualifierWizardStore still sets it; wiring it up is a separate piece of work.
	systemType?: string;
}

export class FormBuilderStore {
	path: string = '';
	value: FormBuilderValue;
	dataSources = new Map<string, { text: string; value: string }[] | string | number | Configuration[]>();
	dataLoading = new Map<string, boolean>();
	previousBlurValues = new Map<string, FormBuilderValueType>();
	configuration: Configuration;
	allProperties: Property[];
	mode: 'create' | 'update';
	mobx = new MobxManager();
	timezone: string;
	isAdvancedOn: boolean;
	inline: boolean;
	rootFormStore?: FormBuilderStore;
	locked: boolean = false;
	showAdvanced: boolean = false;
	subStores = new Map<string, FormBuilderStore[]>();
	// properties the user has actually typed into at least once — sticky for the store's lifetime, same as
	// redux-form's isDirty (reset only on re-initialize, not on blur). Used to stop an automatic dependency-driven
	// reload from clobbering a value the user deliberately entered — ported from the old formBuilder's
	// CHECK_DIRTY_TYPES guard in loadData.js's resetField.
	userEditedProperties = new Set<string>();

	validator: AnnotationValidator;
	getPropertyData: (
		property: Property,
		valueOverride?: (value: FormBuilderValue, propertyFullPath: string) => FormBuilderValue,
	) => ApiRequest<string | { text: string; value: string }[]>;

	constructor(public options: FormBuilderOptions) {
		// options can carry a React node (measuredTargetSlot); deep-observing it breaks React 19's internal
		// owner-stack tracking (Illegal invocation on _debugTask.run when MobX proxies the element objects).
		makeAutoObservable(this, { options: observable.ref });
		this.configuration = options.configuration;
		this.path = options.path || '';
		this.rootFormStore = options.rootFormStore;

		if (this.rootFormStore && this.path) {
			this.value = get(this.rootFormStore.value, this.path) as unknown as FormBuilderValue;
		} else {
			this.value = options.configuration.values;
		}

		if (!this.rootFormStore && options.sessionPersistedValues) {
			for (const id of SESSION_PERSISTED_PROPERTY_IDS) {
				if (this.value[id] == null && options.sessionPersistedValues.values[id] != null) {
					this.value[id] = options.sessionPersistedValues.values[id];
				}
			}
		}

		this.inline = options.inline ?? false;
		this.timezone = options.timezone || ApplicationState.timezone;
		this.getPropertyData = (
			property: Property,
			valueOverride?: (value: FormBuilderValue, propertyFullPath: string) => FormBuilderValue,
		) => {
			const value = this.rootFormStore?.value || this.value;
			const fullPath = [this.path, property.id].filter((x) => x).join('.');
			const valueToUse = valueOverride ? valueOverride(value, fullPath) : value;
			const getPropertyData = options.getPropertyData || this.rootFormStore.options.getPropertyData;
			return getPropertyData(fullPath, valueToUse);
		};
		this.allProperties = flatTree(this.configuration.properties, (x) => x.properties);
		this.fillSubStoresMap();
		this.fillPreviousBlurValues();

		this.validator = new AnnotationValidator(this);

		this.addDebugLog();
		this.mode = options.mode;
		this.load();

		this.mobx.reaction(
			() => this.valid,
			(x) => options.onValidChange?.(x),
			{ fireImmediately: true },
		);
	}

	get userSettingsCategory(): string {
		return this.options.userSettingsCategory ?? this.rootFormStore?.userSettingsCategory ?? null;
	}

	get measuredTargetSlot(): React.ReactNode {
		return this.options.measuredTargetSlot ?? this.rootFormStore?.measuredTargetSlot ?? null;
	}

	get splitterUserSettingsCategory(): string {
		return [this.userSettingsCategory, this.path, 'splitter'].filter((x) => x).join('-');
	}

	get columnsCount() {
		if (this.options.splitter) {
			return 2;
		}
		if (this.options.splitByForms) {
			return this.configuration.forms.length;
		}
		return this.options.columnsCount || 1;
	}

	private addDebugLog() {
		log('value', toJS(this.value));
		log('forms', toJS(this.configuration.forms));
		log(
			'allProperties',
			toJS(
				this.allProperties.map((x) => {
					return {
						id: x.id,
						type: x.type,
						dependsOn: x.dependsOn,
						value: this.value[x.id],
					};
				}),
			),
		);
		if (DEBUG_MODE) {
			this.mobx.reaction(
				() => toJS(this.value),
				(x) => {
					log(`value in path: ${this.path}`, x);
				},
			);
		}
	}

	fillPreviousBlurValues() {
		this.allProperties.forEach((x) => {
			this.previousBlurValues.set(x.id, this.value[x.id]);
		});
	}

	fillSubStoresMap() {
		this.allProperties.forEach((x) => {
			if (x.type === 'CECollection') {
				this.subStores.set(x.id, []);
			}
		});
	}

	registerSubStore(property: Property, store: FormBuilderStore) {
		this.subStores.get(property.id)?.push(store);
	}

	removeSubStore(property: Property, store: FormBuilderStore) {
		this.subStores.get(property.id)?.splice(this.subStores.get(property.id)?.indexOf(store) || 0, 1);
	}

	setAdvanced(value: boolean) {
		this.showAdvanced = value;
	}

	get rootStore(): FormBuilderStore {
		return this.rootFormStore || this;
	}

	get rootValue(): FormBuilderValue {
		return this.rootStore.value;
	}

	get showAdvancedSwitch() {
		return this.allProperties.some((x) => x.advanced === true);
	}

	get valid() {
		return this.validator.valid;
	}

	propertyLabel(propertyId: string) {
		return this.allProperties.find((x) => x.id === propertyId)?.caption ?? propertyId;
	}

	destroy() {
		this.mobx.destroy();
	}

	// generic helper for a field to push a value into another property of this tree (e.g. after an
	// inline "create new" flow) without caring whether that property is actually present — used by
	// CESelection/CEAuthentication's LocalEventsManager handlers.
	setPropertyValueIfExists(propertyId: string, value: FormBuilderValueType, reloadDatasource = false) {
		const property = this.allProperties.find((x) => x.id === propertyId);
		if (!property) return;
		this.value[propertyId] = value;
		if (reloadDatasource) {
			void this.loadDatasource(property, true);
		}
	}

	load = () => {
		flatTree(this.configuration.properties, (x) => x.properties).forEach((property) => {
			if (!hasDataSource(property)) return;
			// defer forms after the first on the initial pass — see the `lazyData` comment on Configuration.
			// unlazyLoad() picks these up (via notLoadedProperties) the moment the first real load completes.
			if (this.configuration.lazyData && this.propertyFormIndex(property) > 0) return;
			void this.loadDatasource(property);
		});
	};

	private unlazyLoad() {
		if (!this.configuration.lazyData) return;
		this.configuration.lazyData = false;
		this.notLoadedProperties.forEach((x) => void this.loadDatasource(x));
	}

	propertyVisible(property: Property) {
		if (property.advanced && !this.showAdvanced) return false;

		return property.visible;
	}

	// shows the spinner only after 200ms (avoids flicker for near-instant responses) and, when this field depends
	// on a field that itself has its own dependency (a chained cascade of dropdowns), waits an extra 1s before
	// firing the request — ported from the old formBuilder's loadData.js spinnerTask fork + upperLevelDependsOn delay.
	private async withDelayedSpinner<T>(property: Property, request: () => Promise<T>): Promise<T> {
		const spinnerTimeout = setTimeout(() => this.dataLoading.set(property.id, true), 200);
		const upperLevelDependsOn = property.dependsOn && this.propertyById(property.dependsOn)?.dependsOn;
		try {
			if (upperLevelDependsOn) {
				await new Promise((resolve) => setTimeout(resolve, 1000));
			}
			return await request();
		} finally {
			clearTimeout(spinnerTimeout);
			this.dataLoading.set(property.id, false);
		}
	}

	async loadDataForTextInput(property: Property) {
		const result = await this.withDelayedSpinner(property, () => apiFetch(this.getPropertyData(property)));
		if (!result.success) {
			if (result.message) {
				this.validator.addLoadError(property, result.message);
			}
		} else {
			// don't clobber a value the user actually typed — only ever apply the server suggestion to a
			// still-pristine field (ported from old formBuilder's CHECK_DIRTY_TYPES guard)
			if (result.data && !this.userEditedProperties.has(property.id)) {
				this.value[property.id] = result.data;
			}
			this.unlazyLoad();
		}
	}

	// on-demand request for a text input with actionMode (the "Query" button) — returns the text to put in the field,
	// or undefined when the request failed.
	async queryTextInput(property: Property): Promise<string | undefined> {
		this.validator.clearLoadError(property);
		const result = await this.withDelayedSpinner(property, () => apiFetch(this.getPropertyData(property)));
		if (!result.success) {
			if (result.message) {
				this.validator.addLoadError(property, result.message);
			}
			return undefined;
		}
		return (result.data as string) ?? '';
	}

	loadDatasource = async (property: Property, force = false) => {
		log('loadDatasource start', property.id);
		this.validator.clearLoadError(property);
		if (this.propertyDisabled(property)) {
			return;
		}
		if (property.type == 'CETextInput') {
			// fields with an action are filled only when the user presses the "Query" button
			if (isActionMode(property.actionMode)) {
				return;
			}
			if (property.dependsOn) {
				void this.loadDataForTextInput(property);
			}
			return;
		}
		if (!hasDataSource(property) || !this.propertyVisible(property)) {
			return;
		}
		if (this.dataSources.get(property.id) && !force) {
			return;
		}

		log('real loadDatasource', toJS(property));

		const result = await this.withDelayedSpinner(property, () => apiFetch(this.getPropertyData(property)));

		if (!result.success) {
			console.log('error', result.details || result.error || result.message);
			if (result.message) {
				this.validator.addLoadError(property, result.message);
			}
		} else {
			this.dataSources.set(property.id, result.data);

			// NOT ported: old formBuilder's loadData.js also read a bonus scalar `data.data` suggested-value off
			// the same response and, for CESelection, auto-applied it (subject to the same dirty-guard as above)
			// via resetField()'s RESETABLE_TYPES handling. This engine's ApiRequest/getPropertyData contract only
			// ever returns a plain options list OR a plain string for a property, never both — porting that piece
			// would need confirming the new endpoints actually carry a suggested value alongside the list, and
			// there's no visible evidence they do. Skipped rather than guessed.
			if (property.type === 'CEAlgorithm' && !this.value[property.id]) {
				const options = result.data as AlgorithmTypeOption[];
				if (options.length) {
					this.value[property.id] = defaultAlgorithmValue(options[0].value);
				}
			}
			this.unlazyLoad();
		}
	};

	propertyLoading(property: Property) {
		return this.dataLoading.get(property.id) ?? false;
	}

	propertyHasVisibleFields(property: Property): boolean {
		if (property.type != 'CEGroup') {
			return this.propertyVisible(property);
		}
		return property.properties.some((prop) => this.propertyHasVisibleFields(prop));
	}

	formIsVisible = (form: Form) => {
		return form.components.some((propId) => {
			const property = this.propertyById(propId);
			return this.propertyHasVisibleFields(property);
		});
	};

	formIsFilled = (form: Form) => {
		return form.components.every((propId) => {
			return this.value[propId] != null;
		});
	};

	propertyIsFilled = (property: Property): boolean => {
		const value = this.value[property.id];
		if (property.type == 'CECollection') {
			return (value as FormBuilderValue[]).length > 0;
		}

		if (property.type != 'CEGroup') {
			if (Array.isArray(value)) return value.length > 0;

			return value != null && value !== '';
		}

		return flatTree(property.properties, (x) => x.properties).every((prop) => {
			return this.propertyIsFilled(prop);
		});
	};

	formProperties = (form: Form) => {
		return flatTree(form.components.map(this.propertyById), (x) => x.properties).filter((x) => x.type != 'CEGroup');
	};

	propertyFormIndex = (property: Property) => {
		return this.configuration.forms.findIndex((form) => this.formProperties(form).includes(property));
	};

	get formLayoutType(): FormLayoutType {
		return this.columnsCount > 1 ? `${this.columnsCount}-columns` : '1-column';
	}

	setGroupSwitch(id: string, value: boolean) {
		this.value[id] = value;
	}

	getGroupSwitch(id: string) {
		return (this.value[id] as boolean) || false;
	}

	getGroupContentDisabled(group: Property) {
		if (!group.switchable) {
			return false;
		}
		return !this.getGroupSwitch(group.id);
	}

	get visibleForms() {
		const forms = this.configuration.forms.filter(this.formIsVisible);
		const columns = this.splitIntoColumns(forms);
		return columns.map((x, i) => {
			return (
				<Section appearance={'none'} key={i} childrenPadding={true} contentPadding={this.options.splitter}>
					{x.map((x) => renderProperty(x, this))}
				</Section>
			);
		});
	}

	getPropertyGroup(property: Property) {
		return this.allProperties.find((x) => x.type == 'CEGroup' && x.properties.find((x) => x.id == property.id));
	}

	splitIntoColumns(forms: Form[]) {
		const columns: Property[][] = Array.from({ length: this.columnsCount }, () => [] as Property[]);

		if (this.options.splitByForms) {
			if (this.options.splitter) {
				const [first, ...rest] = forms;
				columns[0] = first.components.map(this.propertyById);
				columns[1] = rest.map((form) => form.components.map(this.propertyById)).flat();
				return columns;
			}
			return forms.map((form) => form.components.map(this.propertyById));
		}

		const definedColumnsCount = Object.keys(this.options.columnsGroups ?? {}).length;
		const columnsCountForDistribution = this.columnsCount - definedColumnsCount;

		if (this.columnsCount > 1 && this.options.columnsGroups) {
			Object.entries(this.options.columnsGroups).forEach(([index, groups]) => {
				const i = Number(index);
				columns[i] = [];
				groups.forEach((groupId) => {
					const prop = this.allProperties.find((x) => x.id == groupId);
					if (prop) {
						columns[i].push(prop);
					}
				});
			});
		}

		const properties = forms
			.map((form) => form.components.map(this.propertyById))
			.flat()
			.filter((prop) => {
				return columns.every((col) => !col.includes(prop));
			});

		const lengths = properties.map((prop) => {
			if (prop.type != 'CEGroup') {
				return 1;
			}
			return flatTree(prop.properties, (x) => x.properties, null, null, true).length;
		});
		const totalSum = lengths.reduce((acc, value) => acc + value, 0);
		const targetSum = totalSum / columnsCountForDistribution;

		let currentSum = 0;
		let columnIndex = definedColumnsCount;

		// Iterate through the array and split into columns
		properties.forEach((prop, i) => {
			if (currentSum + lengths[i] > targetSum && columnIndex < this.columnsCount - 1) {
				// Move to the next column if adding this object exceeds the target sum
				columnIndex++;
				currentSum = 0;
			}

			columns[columnIndex].push(prop);
			currentSum += lengths[i];
		});

		return columns;
	}

	propertyById = (id: string) => {
		const properties = this.configuration.properties;
		return getItemByKey(properties, id, { idKey: 'id', childrenKey: 'properties' });
	};

	propertyDirectDependencies = (property: Property) => {
		const dependencies = this.allProperties.filter((x) => x.dependsOn == property.id);
		log('propertyDirectDependencies: ', property, 'dependencies: ', dependencies);
		return dependencies;
	};

	propertyDirectDependenciesChain = (property: Property): Property[] => {
		return this.propertyDirectDependencies(property)
			.map((x) => [x, ...this.propertyDirectDependenciesChain(x)])
			.flat();
	};

	propertyAllDependencies = (property: Property) => {
		return this.allProperties.filter(
			(x) => x.dependsOn == property.id || this.propertyAllDependsOn(x).includes(property),
		);
	};

	propertyAllDependsOn = (property: Property) => {
		if (this.configuration.independentForms) {
			return this.allProperties.filter((x) => property.dependsOn == x.id);
		}

		const formIndex = this.propertyFormIndex(property);
		const forms = this.configuration.forms.slice(0, formIndex);
		// all required properties from all previous forms should be filled to enable this property
		const properties = forms.map((form) => this.formProperties(form).filter((x) => x.required)).flat();

		const result = this.allProperties.filter((x) => property.dependsOn == x.id || properties.includes(x));
		log(
			'propertyAllDependsOn, id: ',
			property.id,
			' dependsOn:',
			result.map((x) => toJS(x)),
		);
		return result;
	};

	emptyValueForProperty(property: Property): null | [] | false {
		if (property.type == 'CECheckBox') {
			return false;
		}

		if (property.type == 'CECollection') {
			return [];
		}

		return null;
	}

	clearProperty = (property: Property) => {
		const newValue = this.emptyValueForProperty(property);

		this.value[property.id] = newValue;
		this.previousBlurValues.set(property.id, newValue);
	};

	onPropertyBlur = async (property: Property) => {
		log('onPropertyBlur', property.id);
		if (this.value[property.id] == this.previousBlurValues.get(property.id)) {
			return;
		}

		log('onPropertyBlur', property, toJS(this.propertyDirectDependenciesChain(property)));
		const directDependencies = this.propertyDirectDependenciesChain(property);
		const dependenciesNeededConfirmation = directDependencies.filter(
			(x) => propertiesClearNeededConfirmation.includes(x.type) && this.propertyIsFilled(x),
		);

		if (dependenciesNeededConfirmation.length > 0) {
			const confirmResult = await this.showChangeConfirmation(dependenciesNeededConfirmation);
			if (!confirmResult) {
				this.value[property.id] = this.previousBlurValues.get(property.id);
				return;
			}
		}

		directDependencies.forEach(this.clearProperty);
		directDependencies.forEach((x) => this.loadDatasource(x, true));

		log(
			'onPropertyBlur, id: ',
			property.id,
			' direct dependencies:',
			directDependencies.map((x) => x.id),
		);
		const allDependencies = this.propertyAllDependencies(property);
		this.notLoadedProperties
			.filter((x) => allDependencies.includes(x))
			.forEach((x) => this.loadDatasource(x, true));
		this.previousBlurValues.set(property.id, this.value[property.id]);
	};

	async showChangeConfirmation(dependenciesNeededConfirmation: Property[]) {
		return openConfirmMessage({
			title: i18n('This change needed confirmation'),
			message:
				i18n('This change will clear the following properties: ') +
				dependenciesNeededConfirmation.map((x) => x.caption).join(', '),
			positionType: ModalPosition.Mouse,
			icon: IconType.Warning,
		});
	}

	get notLoadedProperties() {
		return this.allProperties
			.filter(hasDataSource)
			.filter((x) => !this.dataSources.get(x.id) && !this.dataLoading.get(x.id));
	}

	getPropertyTextValue(property: Property) {
		if (['CESelection', 'CESelectionMulti'].includes(property.type)) {
			const value: string[] = Array.isArray(this.value[property.id])
				? (this.value[property.id] as unknown as string[])
				: [this.value[property.id] as string];
			const datasource = (this.dataSources.get(property.id) || []) as { value: string; text: string }[];

			return value
				.map((x) => datasource.find((y: { value: string; text: string }) => y.value == x)?.text ?? x)
				.join(', ');
		}

		if (property.type === 'CEAlgorithm') {
			const algorithm = this.value[property.id] as AlgorithmValue;
			const datasource = (this.dataSources.get(property.id) || []) as AlgorithmTypeOption[];
			return datasource.find((x) => x.value === algorithm?.type)?.text ?? '';
		}

		return this.value[property.id] as string;
	}

	setTextInConfiguration(property: Property) {
		property.text = this.getPropertyTextValue(property);
	}

	onPropertyValueChange = (property: Property, value: FormBuilderValueType) => {
		this.value[property.id] = value;
		this.userEditedProperties.add(property.id);
		this.setTextInConfiguration(property);
		this.validator.clearServerError(property);
		if (value && this.options.sessionPersistedValues && SESSION_PERSISTED_PROPERTY_IDS.has(property.id)) {
			this.options.sessionPersistedValues.values[property.id] = value;
		}
		if (!propertiesOnBlur.includes(property.type)) {
			void this.onPropertyBlur(property);
		}
	};

	propertyDisabled(property: Property) {
		if (this.locked) return true;
		if (this.propertyLoading(property)) return true;
		const propertyGroup = this.getPropertyGroup(property);
		if (propertyGroup && this.getGroupContentDisabled(propertyGroup)) return true;

		const deps = this.propertyAllDependsOn(property);
		log(
			`propertyDisabled, id: %c${property.id}, deps: ${JSON.stringify(deps.map((x) => ({ id: x.id, isFilled: this.propertyIsFilled(x) })))}`,
			'color: green;',
		);
		if (deps.length > 0) {
			return deps.some((x) => {
				return !this.propertyIsFilled(x);
			});
		}
		return false;
	}

	get errors() {
		return this.validator.errors;
	}

	lock() {
		this.locked = true;
	}

	unlock() {
		this.locked = false;
	}
}

export function getPropertyById(properties: Property[], id: string) {
	return getItemByKey(properties, id, { idKey: 'id', childrenKey: 'properties' });
}
